import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSegment,
  listSegments,
  previewSegment,
  recountSegment,
  saveSegment,
} from '@/lib/segments-api';
import type { SegmentGroup } from '@/lib/schemas';

export const segmentKeys = {
  list: () => ['segments'] as const,
  detail: (id: string) => ['segment', id] as const,
  preview: (rule: SegmentGroup) => ['segment-preview', rule] as const,
};

export function useSegments() {
  return useQuery({ queryKey: segmentKeys.list(), queryFn: () => listSegments({ take: 100 }) });
}

export function useSegment(segmentId: string | undefined) {
  return useQuery({
    queryKey: segmentKeys.detail(segmentId ?? 'new'),
    // `skipToken` rather than `enabled`, so the id the query function closes over is narrowed to a
    // string by the same expression that decides whether to run at all.
    queryFn: segmentId === undefined ? skipToken : () => getSegment(segmentId),
  });
}

/**
 * Live count and sample for the rule being edited.
 *
 * Keyed on the RULE itself, so an author who edits a condition and undoes it gets the cached answer
 * instead of a second round trip. `enabled` gates it on the rule having at least one condition,
 * because an empty rule matches nobody server-side and previewing that is a wasted request.
 */
export function useSegmentPreview(brandId: string | undefined, rule: SegmentGroup) {
  return useQuery({
    queryKey: segmentKeys.preview(rule),
    queryFn:
      brandId === undefined || rule.conditions.length === 0
        ? skipToken
        : () => previewSegment({ brandId, rule, sampleSize: 5 }),
    staleTime: 30_000,
  });
}

export function useSaveSegment() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: saveSegment,
    onSuccess: async (saved) => {
      await client.invalidateQueries({ queryKey: segmentKeys.list() });
      await client.invalidateQueries({ queryKey: segmentKeys.detail(saved.id) });
    },
  });
}

export function useRecountSegment() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: recountSegment,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: segmentKeys.list() });
    },
  });
}
